import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
} from '@angular/core'
import Mark from 'mark.js'

import {
  Company,
  ContentRecord,
  Enums,
  GeneratedContentDetails,
  HuntPackageRecord,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools, toolFactory } from '@cyborg-monorepo/hunter/matchboi'
import { categories } from '@cyborg-monorepo/hunter/matchboi'
import { SnackbarService } from '@hyperkit'

import { getToolIcon, supportedToolUrl } from '../../../../helpers/utils'
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service'
import { UpdatedHuntPackage } from '../../../hunt/hunt-template/custom-hunt-package/updated-hunt-package-interface'
import { SearchStateService } from '../../../research/services/search-state.service'
import { CompanyService } from '../../services/company.service'
import { ContentService } from '../../services/content.service'

@Component({
  standalone: false,
  selector: 'cyborg-content',
  templateUrl: './cyborg-content.component.html',
  styleUrls: ['./cyborg-content.component.scss'],
})
export class CyborgContentComponent implements OnInit, AfterViewInit {
  private myElement = inject(ElementRef)
  private searchStateService = inject(SearchStateService)
  private contentService = inject(ContentService)
  private trackingService = inject(TrackingService)
  companyService = inject(CompanyService)
  private cd = inject(ChangeDetectorRef)

  @Input() public content: Partial<ContentRecord>
  @Input() public huntPackage:
    | Partial<HuntPackageRecord>
    | Partial<UpdatedHuntPackage>
  public compatible: boolean
  public contentDetails: Partial<ContentRecord>
  public generatedContentDetails: GeneratedContentDetails
  public generatedAddtionalContentDetails: Array<GeneratedContentDetails> = []
  public hasDeploymentTab = false
  private companyDetails: Company
  public getToolIcon = getToolIcon
  public supportedToolUrl = supportedToolUrl
  public readonly snack = inject(SnackbarService)

  public ngOnInit(): void {
    this.companyService.getCompanyDetails().subscribe((company) => {
      this.companyDetails = company
    })
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.textHightlighting()
    }, 0)
  }

  public onCopy(query: string): void {
    navigator.clipboard.writeText(query)

    this.snack.show('Content Copied', 5000)
  }

  public openDetails(content: Partial<ContentRecord>): void {
    this.contentDetails = content
    this.computeDeploymentTabVisible()
    this.getContent(this.contentDetails)
    if (this.contentDetails?.additional_queries?.length > 0) {
      this.getAdditionalContent(this.contentDetails)
    }
    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_HUNT_PACKAGE_CONTENT_ACCESSED,
      {
        huntPackage: {
          UUID: this.huntPackage.UUID,
          title: this.huntPackage.title,
        },
        content: {
          title: content.title,
          UUID: content.UUID,
          goal: content.goal,
          tool: content.tool,
          platform_type: content.platform_type,
        },
      },
    )
  }

  public getContent(content: Partial<ContentRecord>): void {
    const isCustomPackage = this.isCustomHuntPackage()
    this.contentService
      .getContent(content, isCustomPackage)
      .subscribe((generatedContent) => {
        this.generatedContentDetails = generatedContent
        this.cd.detectChanges()

        this.computeDeploymentTabVisible()
      })
  }

  //Seperate function due to multiple additional queries
  public getAdditionalContent(content: Partial<ContentRecord>): void {
    const isCustomPackage = this.isCustomHuntPackage()
    this.contentService
      .getAdditionalContent(content, isCustomPackage)
      .subscribe((allGeneratedContent) => {
        this.generatedAddtionalContentDetails = allGeneratedContent
        this.cd.detectChanges()

        this.computeDeploymentTabVisible()
      })
  }

  private isCustomHuntPackage(): boolean {
    return (this.huntPackage as any)?.source === Enums.SourceEnum.CUSTOMER
  }

  private computeDeploymentTabVisible(): void {
    const dr = this.contentDetails?.deployment_requirements
    this.hasDeploymentTab = !!(
      dr &&
      ((dr.categories && dr.categories.length > 0) ||
        (dr.searchable_data && dr.searchable_data.length > 0) ||
        (dr.deployment_steps && dr.deployment_steps.length > 0))
    )
  }

  /**
   * Builds the Tool URI based on the contentTool selected and the content of the record
   */
  public getSearchURLForContent(
    contentTool: SupportedTools,
    content: string,
  ): string {
    return toolFactory(contentTool).generateSearchPermalink(
      this.getUserToolRootURL(contentTool),
      content,
    )
  }

  /**
   * Return readable version of default CCM category if it exists, otherwise return user's custom category
   */
  public getReadableCategory(category: string): string {
    return categories[category]?.name ? categories[category].name : category
  }

  /**
   * Returns user's tool root URL for the given tool, or returns null if not set
   */
  public getUserToolRootURL(contentTool: SupportedTools): string | null {
    return this.companyDetails?.toolMetadata?.[contentTool]?.url
  }

  private textHightlighting(): void {
    const context =
      this.myElement.nativeElement.querySelectorAll('p, content-title')
    const instance = new Mark(context)
    const term = this.searchStateService.state.value.term
    if (term) {
      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      })
    }
  }
}
